//Main App component
import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useMemo,
  Suspense,
  lazy,
} from "react";

//Ant Design components
import { Layout, ConfigProvider, App, Spin } from "antd";
import { AnimatePresence } from "framer-motion";

//Ant Design styles
import { configProvider } from "./assets/styles/AppStyles";

//React Router dom
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";

//Reducers
import { appReducer } from "./reducers/appReducer";

//Controllers
import { controller } from "./controllers/app";

//Utils
import { getDefaultThemeColors } from "./utils/defaultThemeColors";
import { 
  getActiveBranchIdInt, 
  syncBranchId 
} from "./utils/branchStorage";

//Error Boundary
import ErrorBoundary from "./components/ErrorBoundary";

//Connection context
import { ConnectionProvider } from "./context/ConnectionContext";

//Currency configuration context
import { CurrencyConfigProvider } from "./context/CurrencyConfigContext";

//Splash Screen
import SplashScreen from "./components/common/SplashScreen";

//Lazy loaded components
const Login = lazy(() => import("./pages/Login"));
const Home = lazy(() => import("./pages/Home"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AuditLogs = lazy(() => import("./pages/AuditLogs")); // ✅ NUEVO
const CertificateVerifyPublic = lazy(() =>
  import("./pages/CertificateVerifyPublic")
);

//App context
export const AppContext = createContext();

// Hook para usar el contexto de la aplicación
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext debe usarse dentro de AppContext.Provider");
  }
  return context;
};

// Loading component
const LoadingSpinner = () => {
  const isDark = localStorage.getItem("algorithm") === "dark";
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: isDark ? "#0a0c10" : "#ffffff",
      }}
    >
      <Spin size="large" />
    </div>
  );
};

const AppRoutes = ({ isAuth, isAuthChecked }) => {
  const location = useLocation();

  // No renderizar rutas hasta que la autenticación haya sido verificada
  if (!isAuthChecked) {
    return null;
  }

  // Verificar si es modo preview del login (permite acceso estando autenticado)
  const isLoginPreview =
    location.pathname.startsWith("/login") &&
    location.search.includes("preview=true");

  return (
    <AnimatePresence mode="wait">
      <Routes location={location}>
        <Route
          path="/verify/:verificationCode"
          element={<CertificateVerifyPublic />}
        />
        {isAuth && !isLoginPreview ? (
          <Route path="/*" element={<Home />} />
        ) : (
          <>
            {/* Login genérico */}
            <Route path="/login" element={<Login />} />
            {/* Login dinámico por sucursal con slug personalizado */}
            <Route path="/login/:branchSlug" element={<Login />} />
            {/* Reset password */}
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route
              path="/app/reset-password/:token"
              element={<ResetPassword />}
            />
            {/* Fallback a login genérico */}
            <Route path="*" element={<Login />} />
          </>
        )}

        {/* Rutas internas protegidas adicionales */}
        {isAuth && (
          <>
            <Route path="/audit-logs" element={<AuditLogs />} />
          </>
        )}
      </Routes>
    </AnimatePresence>
  );
};

const AppRender = () => {
  // Cargar configuraciones desde localStorage
  const savedAppTitle = localStorage.getItem("appTitle") || "ERP";
  const savedAlgorithm = "dark";
  const savedFontSize = localStorage.getItem("fontSize");
  const savedFontFamily = localStorage.getItem("fontFamily");
  const savedComponentSize = localStorage.getItem("componentSize");
  const savedBorderRadius = localStorage.getItem("borderRadius");
  const savedMotion = localStorage.getItem("motion");
  const savedCompact = localStorage.getItem("compact");

  // Cargar colores desde localStorage si existen (para mostrar en login antes de cargar desde API)
  const savedPrimaryColor = localStorage.getItem("primaryColor");
  const savedSecondaryColor = localStorage.getItem("secondaryColor");
  const savedLogo = localStorage.getItem("logo");

  const savedDefaultBranchId = localStorage.getItem("defaultBranchId");
  // ✅ OPTIMIZADO: Usar helper centralizado para obtener branch_id
  const savedActiveBranchId = getActiveBranchIdInt();

  // Cargar colores configurables desde localStorage
  const savedRoleColors = localStorage.getItem("roleColors");
  const savedSemanticColors = localStorage.getItem("semanticColors");
  const savedGradientColors = localStorage.getItem("gradientColors");
  const savedStatusColors = localStorage.getItem("statusColors");

  const defaultColors = getDefaultThemeColors();

  const initialState = {
    isAuth: false,
    isInitializing: true, // Estado de inicialización para mostrar splash
    isAuthChecked: false, // Estado de validación de autenticación
    token: null,
    user: null,
    algorithm: savedAlgorithm,
    branches: [],
    defaultBranchId: savedDefaultBranchId
      ? parseInt(savedDefaultBranchId)
      : null,
    // ✅ OPTIMIZADO: Usar helper centralizado
    currentBranchId: savedActiveBranchId || (savedDefaultBranchId
      ? parseInt(savedDefaultBranchId)
      : null),
    currentBranchTheme: null,
    appTitle: savedAppTitle,
    primaryColor:
      savedPrimaryColor && savedPrimaryColor !== "#000000"
        ? savedPrimaryColor
        : "#1890ff", // Azul por defecto, no negro
    secondaryColor: savedSecondaryColor || "#ffffff",

    logo: savedLogo || null,
    globalLogoType: localStorage.getItem("globalLogoType") || "nucleus",
    fontSize: savedFontSize ? parseInt(savedFontSize) : 14,
    fontFamily: savedFontFamily || undefined,
    componentSize: savedComponentSize || "middle",
    borderRadius: savedBorderRadius ? parseInt(savedBorderRadius) : 6,
    motion: savedMotion !== "false",
    compact: savedCompact === "true",
    // Colores configurables con valores por defecto
    roleColors: savedRoleColors
      ? JSON.parse(savedRoleColors)
      : defaultColors.roleColors,
    semanticColors: savedSemanticColors
      ? JSON.parse(savedSemanticColors)
      : defaultColors.semanticColors,
    gradientColors: savedGradientColors
      ? JSON.parse(savedGradientColors)
      : defaultColors.gradientColors,
    statusColors: savedStatusColors
      ? JSON.parse(savedStatusColors)
      : defaultColors.statusColors,
  };

  const [state, dispatch] = useReducer(appReducer, initialState);

  // Memoizar la configuración del tema para evitar recálculos
  const themeConfig = useMemo(() => configProvider(state), [state]);

  useEffect(() => {
    // ⚡ Validar token y recargar usuario desde API (no usar localStorage obsoleto)
    // Esto asegura datos frescos del backend y validación de sesión
    const initializeApp = async () => {
      dispatch({ type: "SET_INITIALIZING", payload: true });
      dispatch({ type: "SET_AUTH_CHECKED", payload: false });

      const response = await controller.update_app(dispatch);

      // Si es SuperAdmin, forzar tema oscuro y un color primario vibrante pero con buen contraste (AA)
      const user = response?.data?.user;
      if (user?.is_superuser || user?.type_user === "ADM") {
        dispatch({ type: "SET_ALGORITHM", payload: "dark" });
      }

      // Marcar que la autenticación ha sido verificada y quitar splash
      dispatch({ type: "SET_AUTH_CHECKED", payload: true });
      dispatch({ type: "SET_INITIALIZING", payload: false });
    };

    initializeApp();
    
    // ✅ OPTIMIZADO: Sincronizar branch_id al cargar la app
    syncBranchId();
  }, []);

  // Re-initialize when user logs in (after navigation from Login)
  useEffect(() => {
    // Solo re-inicializar si:
    // - El usuario está autenticado
    // - No está inicializando actualmente
    if (state.isAuth && !state.isInitializing) {
      const reinitializeAfterLogin = async () => {
        dispatch({ type: "SET_INITIALIZING", payload: true });

        await controller.update_app(dispatch);

        dispatch({ type: "SET_INITIALIZING", payload: false });
      };

      reinitializeAfterLogin();
    }
  }, [state.isAuth]); // Solo cuando cambia isAuth

  // Actualizar título del documento cuando cambie
  useEffect(() => {
    if (state.appTitle && typeof document !== "undefined") {
      document.title = state.appTitle;
    }
  }, [state.appTitle]);

  useEffect(() => {
    document.body.classList.remove("theme-dark", "theme-light");
    document.body.classList.add(
      state.algorithm === "dark" ? "theme-dark" : "theme-light",
    );
  }, [state.algorithm]);

  // Estilos globales para Tags - Solución definitiva para evitar Tags gigantes
  useEffect(() => {
    const styleId = "global-tag-styles";
    let styleElement = document.getElementById(styleId);

    if (!styleElement) {
      styleElement = document.createElement("style");
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = `
      /* Estilos globales para Tags - Prevenir altura excesiva */
      .ant-tag {
        height: auto !important;
        min-height: auto !important;
        max-height: none !important;
        line-height: 18px !important;
        padding: 2px 8px !important;
        font-size: 11px !important;
        display: inline-flex !important;
        align-items: center !important;
        box-sizing: border-box !important;
        flex-shrink: 0 !important;
        flex-grow: 0 !important;
        width: auto !important;
        max-width: none !important;
        margin: 0 !important;
        vertical-align: middle !important;
      }
      
      .ant-tag-small {
        height: auto !important;
        min-height: auto !important;
        padding: 2px 8px !important;
        font-size: 11px !important;
        line-height: 18px !important;
      }
      
      /* Prevenir expansión en tablas */
      .ant-table td .ant-tag,
      .ant-table td .ant-tag-small {
        height: auto !important;
        min-height: auto !important;
        max-height: 22px !important;
        display: inline-flex !important;
        align-items: center !important;
      }
      
      /* Prevenir expansión en flex containers */
      .ant-space .ant-tag,
      .ant-flex .ant-tag {
        flex-shrink: 0 !important;
        flex-grow: 0 !important;
        width: auto !important;
      }
    `;

    return () => {
      // Cleanup opcional si es necesario
    };
  }, []);

  return (
    <ConfigProvider
      theme={themeConfig.theme}
      locale={themeConfig.locale}
      componentSize={state.componentSize || "middle"}
    >
      <App>
        <ConnectionProvider>
          <CurrencyConfigProvider>
            <AppContext.Provider value={{ state, dispatch }}>
              <ErrorBoundary>
                <Layout>
                  <Router>
                    {/* Mostrar splash screen durante inicialización */}
                    {state.isInitializing && <SplashScreen />}

                    {/* Renderizar rutas solo cuando no esté inicializando */}
                    {!state.isInitializing && (
                      <Suspense fallback={<LoadingSpinner />}>
                        <AppRoutes
                          isAuth={state.isAuth}
                          isAuthChecked={state.isAuthChecked}
                        />
                      </Suspense>
                    )}
                  </Router>
                </Layout>
              </ErrorBoundary>
            </AppContext.Provider>
          </CurrencyConfigProvider>
        </ConnectionProvider>
      </App>
    </ConfigProvider>
  );
};

export default AppRender;
